/**
 * Board #231 -- react-query hooks for the signed-in user's own notification
 * feed.
 *
 * These live next to the page rather than in `@/hooks/useCommunications`
 * because that module's notification hooks describe the WRONG MODEL: they
 * type their results as `Notification` (a `Communication` -- an outbound
 * email/SMS with a channel, a delivery status and a `sent_at`), while the
 * endpoint returns `InAppNotification` rows, which have none of those. Its
 * `useMarkNotificationRead` also POSTs to a route that does not exist. The old
 * hooks are now unused by this page; deleting them is a one-line follow-up
 * once nothing else imports them.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { communicationService, getErrorMessage } from '@/services';
import type { InAppNotificationFilters } from '@/services/communications';

export const NOTIFICATIONS_KEY = ['in-app-notifications'];

export function useInAppNotifications(page: number = 1, filters?: InAppNotificationFilters) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, page, filters],
    queryFn: () => communicationService.getNotifications(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useNotificationEvents() {
  return useQuery({
    queryKey: ['in-app-notification-events'],
    // The catalogue only changes when a producer is added, so it is worth
    // caching for the session rather than refetching per filter open.
    queryFn: () => communicationService.getNotificationEvents(),
    staleTime: 30 * 60 * 1000,
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['in-app-notification-unread-count'],
    queryFn: () => communicationService.getUnreadCount(),
  });
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  queryClient.invalidateQueries({ queryKey: ['in-app-notification-unread-count'] });
}

/** Read AND unread, in one mutation: the board asks for per-notification
 *  read/unread, and a one-way "mark read" cannot undo a misclick. */
export function useSetNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isRead }: { id: string; isRead: boolean }) =>
      communicationService.setRead(id, isRead),
    onSuccess: () => invalidateAll(queryClient),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => communicationService.markAllAsRead(),
    onSuccess: (result) => {
      invalidateAll(queryClient);
      // Report what the server actually did. A flat "All caught up!" after a
      // call that marked nothing is the failure mode this codebase keeps
      // hitting -- success reported with nothing behind it.
      toast.success(
        result.marked === 0
          ? 'Nothing was unread'
          : `${result.marked} notification${result.marked === 1 ? '' : 's'} marked as read`
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
